import { useCallback, useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { FetchHttpClient } from '@/infra/http/fetch-http-client.js';
import { makeAuthController } from '@/main/factories/controllers/auth-controller.js';
import { makeNotificationController } from '@/main/factories/controllers/misc-controllers.js';
import { makeProjectController } from '@/main/factories/controllers/project-controller.js';
import { makeThemeController } from '@/main/factories/controllers/theme-controller.js';
import {
    AppearancePageFactory,
    ClientListPageFactory,
    DashboardPageFactory,
    DevelopmentDetailPageFactory,
    DevelopmentFormPageFactory,
    DevelopmentListPageFactory,
    FirstAccessPageFactory,
    ForgotPasswordPageFactory,
    LoginPageFactory,
    MicrosoftLoginPageFactory,
    MyProfilePageFactory,
    NotificationsPageFactory,
    ProjectCreatePageFactory,
    ProjectEditPageFactory,
    ProjectListPageFactory,
    ReasonListPageFactory,
    ReportsPageFactory,
    ResetPasswordPageFactory,
    UserListPageFactory
} from '@/main/factories/pages/index.js';
import { applyAuthTheme } from '@/presentation/hooks/apply-auth-theme.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { AppShell } from '@/presentation/layouts/app-shell/app-shell.js';
import { ROUTES } from '@/shared/constants/routes.js';

const httpClient = new FetchHttpClient();
const notificationController = makeNotificationController(httpClient);

function PrivateRoute() {
    const { useAuthStore } = useApp();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const location = useLocation();
    return isAuthenticated ? <Outlet /> : <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
    const { useAuthStore } = useApp();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    return !isAuthenticated ? <>{children}</> : <Navigate to={ROUTES.DASHBOARD} replace />;
}

function RequireProfile({ profiles, children }: { profiles: string[]; children: React.ReactNode }) {
    const { useAuthStore } = useApp();
    const profile = useAuthStore((s) => s.user?.profile);
    const isGlobalAdmin = useAuthStore((s) => s.user?.isGlobalAdmin);
    if (isGlobalAdmin || (profile && profiles.includes(profile))) {
        return <>{children}</>;
    }
    return <Navigate to={ROUTES.DASHBOARD} replace />;
}

function ProjectLoader() {
    const { useAuthStore, useProjectStore } = useApp();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const setProjects = useProjectStore((s) => s.setProjects);
    const clear = useProjectStore((s) => s.clear);

    const loadProjects = useCallback(async () => {
        if (!isAuthenticated) {
            clear();
            return;
        }
        const controller = makeProjectController(httpClient);
        const result = await controller.loadMyProjects();
        result.fold(
            () => {},
            (projects) => setProjects(projects)
        );
    }, [isAuthenticated, setProjects, clear]);

    useEffect(() => {
        loadProjects();
    }, [loadProjects]);
    return null;
}

function DefaultThemeLoader() {
    const { useThemeStore } = useApp();
    const setDefaultColors = useThemeStore((s) => s.setDefaultColors);

    useEffect(() => {
        const loadDefaultTheme = async () => {
            const controller = makeThemeController(httpClient);
            const result = await controller.getDefaultTheme();
            result.fold(
                () => {},
                (data) => {
                    if (data.colors != null) {
                        setDefaultColors(data.colors);
                    }
                }
            );
        };
        loadDefaultTheme();
    }, [setDefaultColors]);

    return null;
}

function SessionThemeLoader() {
    const { useAuthStore, useThemeStore } = useApp();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const user = useAuthStore((s) => s.user);
    const setUser = useAuthStore((s) => s.setUser);
    const applyClientTheme = useThemeStore((s) => s.applyClientTheme);
    const resetToDefaultTheme = useThemeStore((s) => s.resetToDefaultTheme);
    const wasAuthenticatedOnMount = useRef(isAuthenticated);
    const sessionLoaded = useRef(false);

    useEffect(() => {
        if (!isAuthenticated) {
            wasAuthenticatedOnMount.current = false;
            sessionLoaded.current = false;
            return;
        }
        if (sessionLoaded.current) {
            return;
        }
        if (!wasAuthenticatedOnMount.current && user) {
            wasAuthenticatedOnMount.current = true;
            sessionLoaded.current = true;
            return;
        }
        wasAuthenticatedOnMount.current = true;
        sessionLoaded.current = true;
        const loadSessionTheme = async () => {
            const controller = makeAuthController(httpClient);
            const result = await controller.getMe();
            result.fold(
                () => {},
                (data) => {
                    setUser(data.user);
                    applyAuthTheme(data.theme, applyClientTheme, resetToDefaultTheme);
                }
            );
        };
        loadSessionTheme();
    }, [isAuthenticated, user, setUser, applyClientTheme, resetToDefaultTheme]);

    return null;
}

export function App() {
    return (
        <BrowserRouter>
            <ProjectLoader />
            <DefaultThemeLoader />
            <SessionThemeLoader />
            <Routes>
                <Route
                    path={ROUTES.LOGIN}
                    element={
                        <PublicRoute>
                            <LoginPageFactory />
                        </PublicRoute>
                    }
                />
                <Route
                    path={ROUTES.LOGIN_MICROSOFT}
                    element={
                        <PublicRoute>
                            <MicrosoftLoginPageFactory />
                        </PublicRoute>
                    }
                />
                <Route
                    path={ROUTES.FORGOT_PASSWORD}
                    element={
                        <PublicRoute>
                            <ForgotPasswordPageFactory />
                        </PublicRoute>
                    }
                />
                <Route
                    path={ROUTES.RESET_PASSWORD}
                    element={
                        <PublicRoute>
                            <ResetPasswordPageFactory />
                        </PublicRoute>
                    }
                />
                <Route
                    path={ROUTES.FIRST_ACCESS}
                    element={
                        <PublicRoute>
                            <FirstAccessPageFactory />
                        </PublicRoute>
                    }
                />

                <Route element={<PrivateRoute />}>
                    <Route element={<AppShell notificationController={notificationController} />}>
                        <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                        <Route path={ROUTES.DASHBOARD} element={<DashboardPageFactory />} />
                        <Route path={ROUTES.PROFILE} element={<MyProfilePageFactory />} />
                        <Route path={ROUTES.DEVELOPMENTS} element={<DevelopmentListPageFactory />} />
                        <Route
                            path={ROUTES.DEVELOPMENT_NEW}
                            element={
                                <RequireProfile profiles={['CF']}>
                                    <DevelopmentFormPageFactory />
                                </RequireProfile>
                            }
                        />
                        <Route path={ROUTES.DEVELOPMENT_DETAIL} element={<DevelopmentDetailPageFactory />} />
                        <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPageFactory />} />
                        <Route
                            path={ROUTES.USERS}
                            element={
                                <RequireProfile profiles={['ADM', 'PMO']}>
                                    <UserListPageFactory />
                                </RequireProfile>
                            }
                        />
                        <Route path={ROUTES.USER_NEW} element={<Navigate to={ROUTES.USERS} replace />} />
                        <Route path={ROUTES.USER_EDIT} element={<Navigate to={ROUTES.USERS} replace />} />
                        <Route path={ROUTES.USER_ASSIGNMENTS} element={<Navigate to={ROUTES.USERS} replace />} />
                        <Route
                            path={ROUTES.CLIENTS}
                            element={
                                <RequireProfile profiles={['ADM', 'PMO']}>
                                    <ClientListPageFactory />
                                </RequireProfile>
                            }
                        />
                        <Route path={ROUTES.CLIENT_NEW} element={<Navigate to={ROUTES.CLIENTS} replace />} />
                        <Route path={ROUTES.CLIENT_EDIT} element={<Navigate to={ROUTES.CLIENTS} replace />} />
                        <Route
                            path={ROUTES.PROJECTS}
                            element={
                                <RequireProfile profiles={['ADM', 'PMO']}>
                                    <ProjectListPageFactory />
                                </RequireProfile>
                            }
                        />
                        <Route
                            path={ROUTES.PROJECT_NEW}
                            element={
                                <RequireProfile profiles={['ADM', 'PMO']}>
                                    <ProjectCreatePageFactory />
                                </RequireProfile>
                            }
                        />
                        <Route
                            path={ROUTES.PROJECT_EDIT}
                            element={
                                <RequireProfile profiles={['ADM', 'PMO']}>
                                    <ProjectEditPageFactory />
                                </RequireProfile>
                            }
                        />
                        <Route
                            path={ROUTES.REASONS}
                            element={
                                <RequireProfile profiles={['ADM']}>
                                    <ReasonListPageFactory />
                                </RequireProfile>
                            }
                        />
                        <Route
                            path={ROUTES.APPEARANCE}
                            element={
                                <RequireProfile profiles={['ADM']}>
                                    <AppearancePageFactory />
                                </RequireProfile>
                            }
                        />
                        <Route
                            path={ROUTES.REPORTS}
                            element={
                                <RequireProfile profiles={['ADM', 'GPA']}>
                                    <ReportsPageFactory />
                                </RequireProfile>
                            }
                        />
                    </Route>
                </Route>

                <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            </Routes>
        </BrowserRouter>
    );
}
